import React from 'react';
import { motion } from 'framer-motion';
import { CalendarXIcon, BatteryLowIcon, RotateCcwIcon, TrophyIcon, HandIcon, ArrowDownIcon, CheckIcon } from 'lucide-react';
const points = [{
  Icon: CalendarXIcon,
  title: 'Fines de semana robados',
  desc: 'Estás cansado de sacrificar tu descanso para escribir planificaciones y fichas técnicas.',
  color: 'text-rose-400',
  bg: 'bg-rose-500/10',
  border: 'border-rose-500/20',
  hover: 'group-hover:border-rose-500/50'
}, {
  Icon: BatteryLowIcon,
  title: 'Energía a cero',
  desc: 'Llegas a casa agotado y todavía tienes que preparar la sesión para el día siguiente.',
  color: 'text-amber-400',
  bg: 'bg-amber-500/10',
  border: 'border-amber-500/20',
  hover: 'group-hover:border-amber-500/50'
}, {
  Icon: RotateCcwIcon,
  title: 'Siempre las mismas actividades',
  desc: 'Sientes que propones siempre los mismos driles y tus alumnos se aburren.',
  color: 'text-purple-400',
  bg: 'bg-purple-500/10',
  border: 'border-purple-500/20',
  hover: 'group-hover:border-purple-500/50'
}, {
  Icon: TrophyIcon,
  title: 'Quieres marcar la diferencia',
  desc: 'Quieres presentar un programa serio y de prestigio ante el club, los padres y los coordinadores.',
  color: 'text-blue-400',
  bg: 'bg-blue-500/10',
  border: 'border-blue-500/20',
  hover: 'group-hover:border-blue-500/50'
}];
export function ForWhomSection() {
  return <section className="w-full bg-bg-dark py-24 px-4 relative overflow-hidden" data-id="element-285">
      {/* Decorative background elements */}
      <div aria-hidden className="absolute top-0 right-0 w-[500px] h-[500px] bg-primary/10 rounded-full blur-[100px] pointer-events-none" data-id="element-286" />
      <div aria-hidden className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-rose-500/5 rounded-full blur-[100px] pointer-events-none" data-id="element-287" />

      <div className="max-w-4xl mx-auto relative z-10" data-id="element-288">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-50px'
      }} transition={{
        duration: 0.5
      }} className="text-center mb-14" data-id="element-289">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-rose-500/10 border border-rose-500/20 mb-6" data-id="element-290">
            <HandIcon className="w-4 h-4 text-rose-400" data-id="element-291" />
            <span className="text-sm font-semibold text-rose-400 tracking-wide uppercase" data-id="element-292">
              Esto es para ti
            </span>
          </div>

          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black text-text-main mb-5 tracking-tight" data-id="element-293">
            ¿Te reconoces?
          </h2>
          <p className="text-xl sm:text-2xl font-bold text-text-main max-w-2xl mx-auto mb-3" data-id="element-294">
            Este recurso es para ti si...
          </p>
          <p className="text-text-sec text-lg max-w-xl mx-auto" data-id="element-295">
            Si te identificas con{' '}
            <span className="text-primary font-semibold" data-id="element-296">2 o más</span> de estas
            situaciones, estás en el lugar correcto.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5 mb-14" data-id="element-297">
          {points.map((point, i) => {
          const {
            Icon
          } = point;
          return <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-50px'
          }} transition={{
            duration: 0.5,
            delay: i * 0.1
          }} whileHover={{
            y: -4
          }} className={`group relative bg-bg-alt/80 backdrop-blur-sm rounded-2xl p-6 border border-border-light ${point.hover} transition-all duration-300 shadow-lg hover:shadow-2xl overflow-hidden`} data-id="element-298">
                {/* Subtle corner glow on hover */}
                <div className={`absolute -top-12 -right-12 w-32 h-32 rounded-full ${point.bg} opacity-0 group-hover:opacity-100 blur-2xl transition-opacity duration-500 pointer-events-none`} data-id="element-299" />

                <div className="relative z-10 flex items-start gap-4" data-id="element-300">
                  <div className={`w-12 h-12 rounded-xl ${point.bg} ${point.color} border ${point.border} flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300`} data-id="element-301">
                    <Icon className="w-6 h-6" strokeWidth={2} data-id="element-302" />
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-303">
                    <h3 className="text-lg font-bold text-text-main mb-1.5 leading-snug" data-id="element-304">
                      {point.title}
                    </h3>
                    <p className="text-text-sec text-base leading-relaxed" data-id="element-305">
                      {point.desc}
                    </p>
                  </div>
                </div>

                {/* "Sí, esto soy yo" indicator on hover */}
                <div className="relative z-10 flex items-center gap-1.5 mt-4 pl-16 opacity-0 -translate-y-1 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-300" data-id="element-306">
                  <div className="w-5 h-5 rounded-full bg-check-green/20 border border-check-green/40 flex items-center justify-center" data-id="element-307">
                    <CheckIcon className="w-3 h-3 text-check-green" strokeWidth={3} data-id="element-308" />
                  </div>
                  <span className="text-xs font-semibold text-check-green uppercase tracking-wide" data-id="element-309">
                    Sí, esto me pasa
                  </span>
                </div>
              </motion.div>;
        })}
        </div>

        {/* Visual transition / closing statement */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-50px'
      }} transition={{
        duration: 0.5,
        delay: 0.2
      }} className="text-center" data-id="element-310">
          <div className="inline-flex flex-col items-center gap-4 px-8 py-8 rounded-3xl bg-gradient-to-b from-primary/10 to-transparent border border-primary/20" data-id="element-311">
            <div className="w-12 h-12 rounded-full bg-primary/20 border border-primary/40 flex items-center justify-center animate-bounce" data-id="element-312">
              <ArrowDownIcon className="w-6 h-6 text-primary" data-id="element-313" />
            </div>
            <p className="text-2xl sm:text-3xl font-black text-text-main max-w-2xl" data-id="element-314">
              Entonces,{' '}
              <span className="bg-gradient-to-r from-primary to-cyan-400 bg-clip-text text-transparent" data-id="element-315">
                MegaPack Natación Listo
              </span>{' '}
              fue creado para ti.
            </p>
          </div>
        </motion.div>
      </div>
    </section>;
}